import {
  ChatRunEventArtifactUpdateschemaArtifactKindEnum,
  ChatRunEventArtifactUpdateschemaUpdateKindEnum,
  TopologyOverlayPatchOpschemaOpEnum
} from '@app/types/sdk';
import type {
  ChatRunEventArtifactUpdateschema,
  TopologyArtifactOverlayStateschema,
  TopologyArtifactOverlayLayerStatusschema,
  TopologyArtifactOverlayStatusDataschema,
  TopologyGraphOverlayschema,
  TopologyOverlayBaseschema,
  TopologyOverlayLayerFilterschema,
  TopologyOverlayLayerschema,
  TopologyOverlayPatchschema,
  TopologyOverlaySelectionschema,
  TopologyV2Edgeschema,
  TopologyV2Graphschema,
  TopologyV2Nodeschema,
  TopologyV2ViewOverlayStateschema,
  TopologyViewPatchOpschema
} from '@app/types/sdk';
import { applyTopologyOpsToGraph } from '@app/pages/org/topology/topologyStream';
import { topologyOverlayBaseMatches, type TopologyArtifactContent } from './topologyArtifact';

export interface TopologyOverlayRuntimeState {
  base?: TopologyOverlayBaseschema;
  revision: number;
  selection: TopologyOverlaySelectionschema;
  layers: TopologyOverlayLayerschema[];
  graphOverlays: TopologyGraphOverlayschema[];
  layerStatuses: Record<string, TopologyArtifactOverlayLayerStatusschema>;
  appliedEventIds: string[];
  localHiddenLayerIds: string[];
  needsReload?: boolean;
}

export interface TopologyArtifactOverlaySnapshot {
  revision: number;
  layerIds: string[];
  selectionNodeIds: string[];
  selectionEdgeIds: string[];
  needsReload?: boolean;
}

export interface TopologyRelatedViewPresentation {
  layerId: string;
  label: string;
  relatedViewId?: string;
  sourceViewId?: string;
  reason?: string;
}

export interface TopologyOverlayPresentation {
  graph: TopologyV2Graphschema;
  relatedView?: TopologyRelatedViewPresentation;
}

type ArtifactUpdateInput = ChatRunEventArtifactUpdateschema & { id?: string; timestamp?: string };

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
};

const toStringArray = (value: unknown): string[] => {
  const raw =
    value instanceof Set
      ? Array.from(value)
      : Array.isArray(value)
        ? value
        : typeof value === 'string'
          ? [value]
          : [];
  return Array.from(new Set(raw.map((item) => (typeof item === 'string' ? item.trim() : '')).filter(Boolean))).sort();
};

const toOrderedStringArray = (value: unknown): string[] => {
  const raw = value instanceof Set ? Array.from(value) : Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
  const seen = new Set<string>();
  const out: string[] = [];
  raw.forEach((item) => {
    const text = typeof item === 'string' ? item.trim() : '';
    if (!text || seen.has(text)) {
      return;
    }
    seen.add(text);
    out.push(text);
  });
  return out;
};

const toNumberArray = (value: unknown): number[] => {
  const raw = Array.isArray(value) ? value : typeof value === 'number' || typeof value === 'string' ? [value] : [];
  const out: number[] = [];
  raw.forEach((item) => {
    const numeric = typeof item === 'number' ? item : typeof item === 'string' ? Number(item) : Number.NaN;
    if (Number.isFinite(numeric)) {
      out.push(Math.max(1, Math.floor(numeric)));
    }
  });
  return out;
};

const toStringSet = (value: unknown): Set<string> => new Set(toStringArray(value));

const cloneSelection = (selection?: TopologyOverlaySelectionschema): TopologyOverlaySelectionschema => ({
  nodeIds: toStringSet(selection?.nodeIds),
  edgeIds: toStringSet(selection?.edgeIds)
});

const cloneLayer = (layer: TopologyOverlayLayerschema): TopologyOverlayLayerschema => ({
  ...layer,
  targets: layer.targets ? [...layer.targets] : undefined,
  effects: layer.effects ? [...layer.effects] : undefined,
  graphOverlay: layer.graphOverlay ? cloneGraphOverlay(layer.graphOverlay) : undefined,
  meta: layer.meta ? { ...layer.meta } : undefined
});

const cloneGraphOverlay = (overlay: TopologyGraphOverlayschema): TopologyGraphOverlayschema => ({
  ...overlay,
  graph: cloneGraph(overlay.graph),
  meta: overlay.meta ? { ...overlay.meta } : undefined
});

const cloneNode = (node: TopologyV2Nodeschema): TopologyV2Nodeschema => ({
  ...node,
  labels: node.labels ? { ...node.labels } : undefined,
  properties: node.properties ? { ...node.properties } : undefined,
  tags: node.tags ? [...node.tags] : undefined,
  refs: node.refs ? [...node.refs] : undefined
});

const cloneEdge = (edge: TopologyV2Edgeschema): TopologyV2Edgeschema => ({
  ...edge,
  labels: edge.labels ? { ...edge.labels } : undefined,
  properties: edge.properties ? { ...edge.properties } : undefined,
  tags: edge.tags ? [...edge.tags] : undefined
});

const cloneGraph = (graph: TopologyV2Graphschema): TopologyV2Graphschema => ({
  ...graph,
  nodes: (graph.nodes ?? []).map(cloneNode),
  edges: (graph.edges ?? []).map(cloneEdge),
  meta: graph.meta ? { ...graph.meta } : undefined
});

export const emptyTopologyOverlayState = (base?: TopologyOverlayBaseschema): TopologyOverlayRuntimeState => ({
  base,
  revision: 0,
  selection: cloneSelection(),
  layers: [],
  graphOverlays: [],
  layerStatuses: {},
  appliedEventIds: [],
  localHiddenLayerIds: []
});

export const hydrateTopologyOverlayState = (
  state: TopologyArtifactOverlayStateschema | undefined | null,
  fallbackBase?: TopologyOverlayBaseschema
): TopologyOverlayRuntimeState => {
  if (!state) {
    return emptyTopologyOverlayState(fallbackBase);
  }
  return {
    base: state.base ?? fallbackBase,
    revision: Number.isFinite(state.revision) ? Math.max(0, Math.floor(state.revision)) : 0,
    selection: cloneSelection(state.selection),
    layers: (state.layers ?? []).map(cloneLayer),
    graphOverlays: (state.graphOverlays ?? []).map(cloneGraphOverlay),
    layerStatuses: {},
    appliedEventIds: [],
    localHiddenLayerIds: []
  };
};

export const hydrateTopologyViewOverlayState = (
  state: TopologyV2ViewOverlayStateschema | undefined | null,
  fallbackBase?: TopologyOverlayBaseschema
): TopologyOverlayRuntimeState => {
  if (!state) {
    return emptyTopologyOverlayState(fallbackBase);
  }
  return {
    base: state.base ?? fallbackBase,
    revision: 0,
    selection: cloneSelection(),
    layers: (state.layers ?? []).map(cloneLayer),
    graphOverlays: (state.graphOverlays ?? []).map(cloneGraphOverlay),
    layerStatuses: {},
    appliedEventIds: [],
    localHiddenLayerIds: []
  };
};

const markApplied = (state: TopologyOverlayRuntimeState, eventId?: string): TopologyOverlayRuntimeState => {
  if (!eventId || state.appliedEventIds.includes(eventId)) {
    return state;
  }
  return {
    ...state,
    appliedEventIds: [...state.appliedEventIds, eventId].slice(-100)
  };
};

const upsertById = <T extends { id: string }>(items: T[], item: T): T[] => {
  const index = items.findIndex((existing) => existing.id === item.id);
  if (index < 0) {
    return [...items, item];
  }
  const next = [...items];
  next[index] = item;
  return next;
};

const layerSource = (layer: TopologyOverlayLayerschema): string => String(layer.meta?.source ?? '');

const layerMatchesFilter = (layer: TopologyOverlayLayerschema, filter?: TopologyOverlayLayerFilterschema): boolean => {
  const layerIds = toStringArray(filter?.layerIds);
  const kinds = toStringArray(filter?.kinds);
  const source = typeof filter?.source === 'string' ? filter.source.trim() : '';

  if (layerIds.length === 0 && kinds.length === 0 && !source) {
    return layerSource(layer) === 'chat';
  }
  if (layerIds.length > 0 && !layerIds.includes(layer.id)) {
    return false;
  }
  if (kinds.length > 0 && !kinds.includes(layer.kind)) {
    return false;
  }
  if (source && layerSource(layer) !== source) {
    return false;
  }
  return true;
};

const removeLayers = (
  state: TopologyOverlayRuntimeState,
  predicate: (layer: TopologyOverlayLayerschema) => boolean
): TopologyOverlayRuntimeState => {
  const removed = state.layers.filter(predicate);
  if (removed.length === 0) {
    return state;
  }
  const removedOverlayIds = new Set<string>();
  removed.forEach((layer) => {
    removedOverlayIds.add(layer.id);
    if (layer.graphOverlay?.id) {
      removedOverlayIds.add(layer.graphOverlay.id);
    }
  });
  const layers = state.layers.filter((layer) => !predicate(layer));
  const remainingOverlayIds = new Set(layers.map((layer) => layer.graphOverlay?.id).filter(Boolean) as string[]);
  return {
    ...state,
    layers,
    graphOverlays: state.graphOverlays.filter((overlay) => !removedOverlayIds.has(overlay.id) || remainingOverlayIds.has(overlay.id)),
    layerStatuses: Object.fromEntries(
      Object.entries(state.layerStatuses).filter(([layerId]) => layers.some((layer) => layer.id === layerId))
    ),
    localHiddenLayerIds: state.localHiddenLayerIds.filter((layerId) => layers.some((layer) => layer.id === layerId))
  };
};

const applyOverlayOps = (
  state: TopologyOverlayRuntimeState,
  patch: TopologyOverlayPatchschema,
  revision: number
): TopologyOverlayRuntimeState => {
  let next: TopologyOverlayRuntimeState = {
    ...state,
    base: patch.base ?? state.base,
    needsReload: state.needsReload || revision > state.revision + 1
  };

  (patch.ops ?? []).forEach((op: any) => {
    switch (op.op) {
      case TopologyOverlayPatchOpschemaOpEnum.UpsertLayer: {
        if (!op.layer?.id) {
          return;
        }
        const layer = cloneLayer(op.layer);
        next = {
          ...next,
          layers: upsertById(next.layers, layer),
          graphOverlays: layer.graphOverlay ? upsertById(next.graphOverlays, cloneGraphOverlay(layer.graphOverlay)) : next.graphOverlays,
          localHiddenLayerIds: next.localHiddenLayerIds.filter((layerId) => layerId !== layer.id)
        };
        return;
      }
      case TopologyOverlayPatchOpschemaOpEnum.RemoveLayer:
        if (!op.layerId) {
          return;
        }
        next = removeLayers(next, (layer) => layer.id === op.layerId);
        return;
      case TopologyOverlayPatchOpschemaOpEnum.ClearLayers:
        next = removeLayers(next, (layer) => layerMatchesFilter(layer, op.filter));
        return;
      case TopologyOverlayPatchOpschemaOpEnum.SetSelection:
        next = {
          ...next,
          selection: cloneSelection(op.selection)
        };
        return;
      case TopologyOverlayPatchOpschemaOpEnum.UpsertGraphOverlay:
        if (!op.graphOverlay?.id) {
          return;
        }
        next = {
          ...next,
          graphOverlays: upsertById(next.graphOverlays, cloneGraphOverlay(op.graphOverlay))
        };
        return;
      default:
        return;
    }
  });

  return {
    ...next,
    revision
  };
};

export const applyTopologyOverlayPatch = (
  state: TopologyOverlayRuntimeState,
  patch: TopologyOverlayPatchschema,
  options: { eventId?: string; revision?: number } = {}
): TopologyOverlayRuntimeState => {
  const eventId = options.eventId;
  if (eventId && state.appliedEventIds.includes(eventId)) {
    return state;
  }
  const revision =
    typeof options.revision === 'number' && Number.isFinite(options.revision)
      ? Math.max(0, Math.floor(options.revision))
      : state.revision + 1;
  if (revision <= state.revision) {
    return markApplied(state, eventId);
  }

  return markApplied(applyOverlayOps(state, patch, revision), eventId);
};

export const applyTopologyOverlayStatus = (
  state: TopologyOverlayRuntimeState,
  status: TopologyArtifactOverlayStatusDataschema
): TopologyOverlayRuntimeState => {
  if (typeof status.overlayRevision === 'number' && status.overlayRevision !== state.revision) {
    return state;
  }
  const layers = Array.isArray(status.layers) ? status.layers : [];
  if (layers.length === 0) {
    return {
      ...state,
      layerStatuses: {}
    };
  }
  const knownLayerIds = new Set(state.layers.map((layer) => layer.id));
  const next = { ...state.layerStatuses };
  layers.forEach((layerStatus) => {
    const layerId = typeof layerStatus.layerId === 'string' ? layerStatus.layerId.trim() : '';
    if (!layerId || !knownLayerIds.has(layerId)) {
      return;
    }
    next[layerId] = layerStatus;
  });
  return {
    ...state,
    layerStatuses: next
  };
};

export const setTopologyOverlayLayerStatuses = (
  state: TopologyOverlayRuntimeState,
  layers: TopologyArtifactOverlayLayerStatusschema[] | undefined | null
): TopologyOverlayRuntimeState => {
  if (!Array.isArray(layers) || layers.length === 0) {
    return {
      ...state,
      layerStatuses: {}
    };
  }
  const knownLayerIds = new Set(state.layers.map((layer) => layer.id));
  const next: Record<string, TopologyArtifactOverlayLayerStatusschema> = {};
  layers.forEach((layerStatus) => {
    const layerId = typeof layerStatus.layerId === 'string' ? layerStatus.layerId.trim() : '';
    if (!layerId || !knownLayerIds.has(layerId)) {
      return;
    }
    next[layerId] = layerStatus;
  });
  return {
    ...state,
    layerStatuses: next
  };
};

export const applyTopologyArtifactUpdate = (
  state: TopologyOverlayRuntimeState,
  update: ArtifactUpdateInput,
  topology: TopologyArtifactContent
): TopologyOverlayRuntimeState => {
  if (update.artifactKind !== ChatRunEventArtifactUpdateschemaArtifactKindEnum.Topology) {
    return state;
  }
  if (update.updateKind !== ChatRunEventArtifactUpdateschemaUpdateKindEnum.TopologyOverlayPatch) {
    return state;
  }
  if (!update.patch || !topologyOverlayBaseMatches(topology, update.patch.base)) {
    return state;
  }
  return applyTopologyOverlayPatch(state, update.patch, {
    eventId: update.eventId ?? update.id,
    revision: update.revision
  });
};

export const setTopologyLayerLocalVisibility = (
  state: TopologyOverlayRuntimeState,
  layerId: string,
  visible: boolean
): TopologyOverlayRuntimeState => {
  const hidden = new Set(state.localHiddenLayerIds);
  if (visible) {
    hidden.delete(layerId);
  } else {
    hidden.add(layerId);
  }
  const next = Array.from(hidden).sort();
  if (next.join('\n') === state.localHiddenLayerIds.join('\n')) {
    return state;
  }
  return {
    ...state,
    localHiddenLayerIds: next
  };
};

const mergeStringMap = (
  left: Record<string, string> | undefined,
  right: Record<string, string> | undefined
): Record<string, string> | undefined => {
  if (!left && !right) {
    return undefined;
  }
  return { ...(left ?? {}), ...(right ?? {}) };
};

const mergeRuntimeUI = (
  left: Record<string, unknown> | undefined,
  right: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  if (!left && !right) {
    return undefined;
  }
  const next: Record<string, unknown> = { ...(left ?? {}), ...(right ?? {}) };
  const leftRuntime = asRecord(left?.runtimeOverlays);
  const rightRuntime = asRecord(right?.runtimeOverlays);
  if (leftRuntime || rightRuntime) {
    next.runtimeOverlays = { ...(leftRuntime ?? {}), ...(rightRuntime ?? {}) };
  }
  return next;
};

type RuntimeOverlayLayerMeta = {
  deliveryMode?: string;
};

const runtimeOverlayLayerMeta = (
  layers: TopologyOverlayLayerschema[],
  layerStatuses: Record<string, TopologyArtifactOverlayLayerStatusschema>
): Record<string, RuntimeOverlayLayerMeta> => {
  const out: Record<string, RuntimeOverlayLayerMeta> = {};
  layers.forEach((layer) => {
    const deliveryMode =
      (typeof layer.runtime?.deliveryMode === 'string' ? layer.runtime.deliveryMode.trim() : '') ||
      (typeof layerStatuses[layer.id]?.deliveryMode === 'string' ? layerStatuses[layer.id]?.deliveryMode?.trim() : '');
    if (!deliveryMode) {
      return;
    }
    out[layer.id] = { deliveryMode };
  });
  return out;
};

const decorateRuntimeOverlayUI = (
  ui: Record<string, unknown> | undefined,
  layerMetaById: Record<string, RuntimeOverlayLayerMeta>
): Record<string, unknown> | undefined => {
  if (!ui) {
    return ui;
  }
  const runtimeOverlays = asRecord(ui.runtimeOverlays);
  if (!runtimeOverlays || Object.keys(layerMetaById).length === 0) {
    return ui;
  }
  let changed = false;
  const nextRuntimeOverlays: Record<string, unknown> = { ...runtimeOverlays };
  Object.entries(runtimeOverlays).forEach(([layerId, raw]) => {
    const meta = layerMetaById[layerId];
    const overlay = asRecord(raw);
    if (!meta || !overlay) {
      return;
    }
    const nextOverlay = { ...overlay };
    if (meta.deliveryMode && overlay.deliveryMode !== meta.deliveryMode) {
      nextOverlay.deliveryMode = meta.deliveryMode;
      changed = true;
    }
    nextRuntimeOverlays[layerId] = nextOverlay;
  });
  if (!changed) {
    return ui;
  }
  return {
    ...ui,
    runtimeOverlays: nextRuntimeOverlays
  };
};

const decorateRuntimeOverlayGraph = (
  graph: TopologyV2Graphschema,
  layers: TopologyOverlayLayerschema[],
  layerStatuses: Record<string, TopologyArtifactOverlayLayerStatusschema>
): TopologyV2Graphschema => {
  const layerMetaById = runtimeOverlayLayerMeta(layers, layerStatuses);
  if (Object.keys(layerMetaById).length === 0) {
    return graph;
  }
  let changed = false;
  const nodes = (graph.nodes ?? []).map((node) => {
    const properties = node.properties ? { ...node.properties } : undefined;
    const ui = decorateRuntimeOverlayUI(asRecord(properties?.ui), layerMetaById);
    if (ui === properties?.ui) {
      return node;
    }
    changed = true;
    return {
      ...node,
      properties: {
        ...(properties ?? {}),
        ui
      }
    };
  });
  const edges = (graph.edges ?? []).map((edge) => {
    const properties = edge.properties ? { ...edge.properties } : undefined;
    const ui = decorateRuntimeOverlayUI(asRecord(properties?.ui), layerMetaById);
    if (ui === properties?.ui) {
      return edge;
    }
    changed = true;
    return {
      ...edge,
      properties: {
        ...(properties ?? {}),
        ui
      }
    };
  });
  if (!changed) {
    return graph;
  }
  return {
    ...graph,
    ...(graph.nodes !== undefined ? { nodes } : {}),
    ...(graph.edges !== undefined ? { edges } : {})
  };
};

type TopologyPathMembership = {
  pathIds: string[];
  ranks: number[];
  primary: boolean;
  shared: boolean;
};

const pathMembershipFromProperties = (properties: Record<string, unknown> | undefined): TopologyPathMembership | undefined => {
  const topologyPath = asRecord(properties?.topologyPath);
  const ui = asRecord(properties?.ui);
  const source = topologyPath ?? ui;
  if (!source) {
    return undefined;
  }
  const pathIds = toOrderedStringArray(source.pathIds);
  if (pathIds.length === 0) {
    return undefined;
  }
  const ranks = toNumberArray(topologyPath?.ranks ?? ui?.pathRanks);
  return {
    pathIds,
    ranks,
    primary: source.primary === true || ui?.primaryPath === true,
    shared: source.shared === true || ui?.sharedAcrossPaths === true || pathIds.length > 1
  };
};

const pathMembershipTone = (membership: TopologyPathMembership): string => {
  if (membership.shared) {
    return 'warning';
  }
  const rank = membership.ranks[0] ?? 1;
  switch ((rank - 1) % 4) {
    case 0:
      return 'info';
    case 1:
      return 'success';
    case 2:
      return 'warning';
    default:
      return 'critical';
  }
};

const pathMembershipLabel = (membership: TopologyPathMembership): { label: string; value: string } => {
  if (membership.shared) {
    const ranks = membership.ranks.length > 0 ? membership.ranks.map((rank) => `P${rank}`).join('/') : membership.pathIds.join('/');
    return { label: 'Shared paths', value: ranks };
  }
  const rank = membership.ranks[0] ?? 1;
  return { label: `Path ${rank}`, value: `P${rank}` };
};

const mergeEffects = (left: unknown, right: string[]): string[] => {
  const existing = Array.isArray(left) ? left.map((item) => (typeof item === 'string' ? item.trim() : '')).filter(Boolean) : [];
  return Array.from(new Set([...existing, ...right])).sort();
};

const decoratePathMembershipProperties = (
  properties: Record<string, unknown> | undefined,
  layer: TopologyOverlayLayerschema,
  targetEffects: string[]
): Record<string, unknown> | undefined => {
  const membership = pathMembershipFromProperties(properties);
  if (!membership) {
    return properties;
  }
  const ui = { ...(asRecord(properties?.ui) ?? {}) };
  const runtimeOverlays = { ...(asRecord(ui.runtimeOverlays) ?? {}) };
  const existing = asRecord(runtimeOverlays[layer.id]) ?? {};
  const label = pathMembershipLabel(membership);
  runtimeOverlays[layer.id] = {
    ...existing,
    kind: 'path',
    layerId: layer.id,
    pathIds: membership.pathIds,
    ranks: membership.ranks,
    primary: membership.primary,
    shared: membership.shared,
    label: typeof existing.label === 'string' ? existing.label : label.label,
    value: existing.value ?? label.value,
    tone: typeof existing.tone === 'string' ? existing.tone : pathMembershipTone(membership),
    effects: mergeEffects(existing.effects, targetEffects)
  };
  return {
    ...(properties ?? {}),
    ui: {
      ...ui,
      highlighted: ui.highlighted ?? true,
      kind: ui.kind ?? 'path',
      pathIds: ui.pathIds ?? membership.pathIds,
      pathRanks: ui.pathRanks ?? membership.ranks,
      sharedAcrossPaths: ui.sharedAcrossPaths ?? membership.shared,
      runtimeOverlays
    }
  };
};

const visiblePathLayers = (layers: TopologyOverlayLayerschema[]): TopologyOverlayLayerschema[] =>
  layers.filter((layer) => layer.kind === 'path' || Boolean(layer.path));

const decoratePathMembershipGraph = (
  graph: TopologyV2Graphschema,
  layers: TopologyOverlayLayerschema[]
): TopologyV2Graphschema => {
  const pathLayers = visiblePathLayers(layers);
  if (pathLayers.length === 0) {
    return graph;
  }
  let changed = false;
  const layer = pathLayers[pathLayers.length - 1];
  const nodes = (graph.nodes ?? []).map((node) => {
    const properties = decoratePathMembershipProperties(node.properties, layer, ['node_badge', 'node_ring', 'hover_rows']);
    if (properties === node.properties) {
      return node;
    }
    changed = true;
    return { ...node, properties };
  });
  const edges = (graph.edges ?? []).map((edge) => {
    const properties = decoratePathMembershipProperties(edge.properties, layer, ['edge_badge', 'edge_color', 'edge_width', 'hover_rows']);
    if (properties === edge.properties) {
      return edge;
    }
    changed = true;
    return { ...edge, properties };
  });
  if (!changed) {
    return graph;
  }
  return {
    ...graph,
    ...(graph.nodes !== undefined ? { nodes } : {}),
    ...(graph.edges !== undefined ? { edges } : {})
  };
};

const mergeAnyMap = (
  left: Record<string, unknown> | undefined,
  right: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  if (!left && !right) {
    return undefined;
  }
  const next: Record<string, unknown> = { ...(left ?? {}), ...(right ?? {}) };
  const leftUI = asRecord(left?.ui);
  const rightUI = asRecord(right?.ui);
  if (leftUI || rightUI) {
    next.ui = mergeRuntimeUI(leftUI, rightUI);
  }
  return next;
};

const mergeTags = (left?: string[], right?: string[]): string[] | undefined => {
  const values = Array.from(new Set([...(left ?? []), ...(right ?? [])].filter(Boolean))).sort();
  return values.length > 0 ? values : undefined;
};

const mergeNode = (base: TopologyV2Nodeschema | undefined, incoming: TopologyV2Nodeschema): TopologyV2Nodeschema => {
  if (!base) {
    return cloneNode(incoming);
  }
  return {
    ...base,
    ...incoming,
    labels: mergeStringMap(base.labels, incoming.labels),
    properties: mergeAnyMap(base.properties, incoming.properties),
    tags: mergeTags(base.tags, incoming.tags)
  };
};

const mergeEdge = (base: TopologyV2Edgeschema | undefined, incoming: TopologyV2Edgeschema): TopologyV2Edgeschema => {
  if (!base) {
    return cloneEdge(incoming);
  }
  return {
    ...base,
    ...incoming,
    labels: mergeStringMap(base.labels, incoming.labels),
    properties: mergeAnyMap(base.properties, incoming.properties),
    tags: mergeTags(base.tags, incoming.tags)
  };
};

const mergeGraphOverlays = (
  graph: TopologyV2Graphschema,
  graphOverlays: TopologyGraphOverlayschema[]
): TopologyV2Graphschema => {
  if (graphOverlays.length === 0) {
    return cloneGraph(graph);
  }
  const nodes = new Map<string, TopologyV2Nodeschema>();
  const edges = new Map<string, TopologyV2Edgeschema>();
  (graph.nodes ?? []).forEach((node) => nodes.set(node.id, cloneNode(node)));
  (graph.edges ?? []).forEach((edge) => edges.set(edge.id, cloneEdge(edge)));

  graphOverlays.forEach((overlay) => {
    (overlay.graph.nodes ?? []).forEach((node: any) => nodes.set(node.id, mergeNode(nodes.get(node.id), node)));
    (overlay.graph.edges ?? []).forEach((edge: any) => edges.set(edge.id, mergeEdge(edges.get(edge.id), edge)));
  });

  return {
    ...graph,
    nodes: Array.from(nodes.values()),
    edges: Array.from(edges.values())
  };
};

const isHidden = (properties: Record<string, unknown> | undefined): boolean => {
  const nested = asRecord(properties?.ui);
  return properties?.['ui.hidden'] === true || properties?.['ui.hidden'] === 'true' || nested?.hidden === true || nested?.hidden === 'true';
};

const filterHiddenElements = (graph: TopologyV2Graphschema): TopologyV2Graphschema => {
  const nodes = (graph.nodes ?? []).filter((node) => !isHidden(node.properties));
  const nodeIds = new Set(nodes.map((node) => node.id));
  const edges = (graph.edges ?? []).filter(
    (edge) => !isHidden(edge.properties) && nodeIds.has(edge.fromId) && nodeIds.has(edge.toId)
  );
  return {
    ...graph,
    nodes,
    edges
  };
};

export const deriveTopologyOverlayGraph = (
  graph: TopologyV2Graphschema,
  state: TopologyOverlayRuntimeState
): TopologyV2Graphschema => {
  const orderedLayers = visibleOrderedLayers(state);

  return deriveTopologyOverlayGraphForLayers(graph, state, orderedLayers);
};

const visibleOrderedLayers = (state: TopologyOverlayRuntimeState): TopologyOverlayLayerschema[] => {
  const hidden = new Set(state.localHiddenLayerIds);
  return [...state.layers]
    .filter((layer) => layer.visible !== false && !hidden.has(layer.id))
    .sort((left, right) => {
      const byPriority = (left.priority ?? 0) - (right.priority ?? 0);
      return byPriority !== 0 ? byPriority : left.id.localeCompare(right.id);
    });
};

const deriveTopologyOverlayGraphForLayers = (
  graph: TopologyV2Graphschema,
  state: TopologyOverlayRuntimeState,
  orderedLayers: TopologyOverlayLayerschema[]
): TopologyV2Graphschema => {
  let next = mergeGraphOverlays(graph, state.graphOverlays);
  orderedLayers.forEach((layer) => {
    if (layer.effects?.length) {
      next = applyTopologyOpsToGraph(next, layer.effects as TopologyViewPatchOpschema[]);
    }
  });
  next = decoratePathMembershipGraph(next, orderedLayers);
  next = decorateRuntimeOverlayGraph(next, orderedLayers, state.layerStatuses);
  return filterHiddenElements(next);
};

const selectedRelatedViewLayer = (layers: TopologyOverlayLayerschema[]): TopologyOverlayLayerschema | undefined => {
  const relatedLayers = layers.filter(
    (layer) => layer.path?.renderDecision?.presentation === 'related_view' && Boolean(layer.graphOverlay?.graph)
  );
  return relatedLayers[relatedLayers.length - 1];
};

export const deriveTopologyOverlayPresentation = (
  graph: TopologyV2Graphschema,
  state: TopologyOverlayRuntimeState
): TopologyOverlayPresentation => {
  const orderedLayers = visibleOrderedLayers(state);
  const relatedLayer = selectedRelatedViewLayer(orderedLayers);
  if (!relatedLayer?.graphOverlay?.graph) {
    return { graph: deriveTopologyOverlayGraphForLayers(graph, state, orderedLayers) };
  }

  let relatedGraph = cloneGraph(relatedLayer.graphOverlay.graph);
  relatedGraph = decoratePathMembershipGraph(relatedGraph, [relatedLayer]);
  relatedGraph = decorateRuntimeOverlayGraph(relatedGraph, [relatedLayer], state.layerStatuses);
  relatedGraph = filterHiddenElements(relatedGraph);

  return {
    graph: relatedGraph,
    relatedView: {
      layerId: relatedLayer.id,
      label: relatedLayer.label,
      relatedViewId: relatedLayer.path?.renderDecision?.relatedViewId ?? relatedLayer.graphOverlay.viewId,
      sourceViewId: relatedLayer.graphOverlay.sourceViewId,
      reason: relatedLayer.path?.renderDecision?.reason
    }
  };
};

export const topologyOverlaySnapshot = (state: TopologyOverlayRuntimeState): TopologyArtifactOverlaySnapshot => ({
  revision: state.revision,
  layerIds: state.layers.map((layer) => layer.id).sort(),
  selectionNodeIds: toStringArray(state.selection.nodeIds),
  selectionEdgeIds: toStringArray(state.selection.edgeIds),
  needsReload: state.needsReload || undefined
});
